import React, { useState, useEffect } from 'react';
import { useLanguage } from '../hooks/useLanguage';
import { Menu, X } from 'lucide-react';

import { useLocation, useNavigate } from 'react-router-dom';

const Navbar = ({ forceSolid }) => {
  const { language, toggleLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isHome = location.pathname === '/';
  const shouldShowSolid = forceSolid || !isHome || isScrolled;

  // Close menu when clicking a link
  const closeMenu = () => setIsMenuOpen(false);

  // Unified navigation handler
  const handleNavigation = (e, sectionId) => {
    e.preventDefault();
    closeMenu();

    if (isHome) {
      // If on home, just scroll
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // If not on home, navigate to home with hash
      // We use a slight timeout to allow the navigation to happen before scrolling
      navigate('/', { state: { scrollTo: sectionId } });
    }
  };

  return (
    <nav className={`fixed top-0 w-full z-[1000] transition-all duration-300 ${shouldShowSolid ? 'bg-slate-900 shadow-md py-4' : 'bg-transparent py-6'} text-white`}>
      <div className="container mx-auto px-4 flex justify-between items-center">
        {/* Brand */}
        <a href="/" className="font-bold text-xl md:text-2xl tracking-wider font-mono z-50 hover:text-gold-500 transition-colors">
          NOPA Engineering
        </a>

        {/* Desktop Menu */}
        <ul className="hidden md:flex desktop-menu-hidden items-center gap-8 list-none">
          <li><a href="#about" onClick={(e) => handleNavigation(e, 'about')} className="hover:text-gold-500 transition-colors">{t.nav.about}</a></li>
          <li><a href="#services" onClick={(e) => handleNavigation(e, 'services')} className="hover:text-gold-500 transition-colors">{t.nav.services}</a></li>
          <li>
            <a href="#projects" onClick={(e) => handleNavigation(e, 'projects')} className="hover:text-gold-500 transition-colors">
              {t.nav.projects}
            </a>
          </li>
          <li><a href="#contact" onClick={(e) => handleNavigation(e, 'contact')} className="hover:text-gold-500 transition-colors">{t.nav.contact}</a></li>
          <li>
            <button
              onClick={() => toggleLanguage(language === 'sr' ? 'en' : 'sr')}
              className="border border-white hover:border-gold-500 hover:text-gold-500 px-2 py-1 rounded transition-all font-mono"
            >
              {language === 'sr' ? 'EN' : 'SR'}
            </button>
          </li>
        </ul>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden z-50 text-white focus:outline-none"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          {isMenuOpen ? <X className="w-8 h-8" /> : <Menu className="w-8 h-8" />}
        </button>

        {/* Mobile Menu Overlay */}
        <div className={`fixed inset-0 bg-slate-950/95 backdrop-blur-sm z-40 flex flex-col items-center justify-center transition-transform duration-300 ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'} md:hidden`}>
          <ul className="flex flex-col items-center gap-8 text-xl font-medium">
            <li><a href="#about" onClick={(e) => handleNavigation(e, 'about')} className="hover:text-gold-500 transition-colors">{t.nav.about}</a></li>
            <li><a href="#services" onClick={(e) => handleNavigation(e, 'services')} className="hover:text-gold-500 transition-colors">{t.nav.services}</a></li>
            <li><a href="#projects" onClick={(e) => handleNavigation(e, 'projects')} className="hover:text-gold-500 transition-colors">{t.nav.projects}</a></li>
            <li><a href="#contact" onClick={(e) => handleNavigation(e, 'contact')} className="hover:text-gold-500 transition-colors">{t.nav.contact}</a></li>
            <li>
              <button
                onClick={() => {
                  toggleLanguage(language === 'sr' ? 'en' : 'sr');
                  closeMenu();
                }}
                className="border border-white hover:border-gold-500 hover:text-gold-500 px-4 py-2 rounded transition-all"
              >
                {language === 'sr' ? 'English' : 'Srpski'}
              </button>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
